// network/axiosInstance.ts
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { tokenStore } from './authentication/token_store';
import { AppRoutes } from '../common/routes/app_routes';
import { isPublicPath } from '../common/routes/route_utils';
import {
  Configuration,
  UserApi,
  AuthenticationSessionApi,
  AuthenticationPasswordApi,
  AuthenticationOTPApi,
  ProductsApi,
  PartnersApi,
  SalesApi,
  DocumentsApi,
  AdminUsersApi,
  CategoriesApi,
  AdminCategoriesApi,
  BrandsApi,
  PaymentsApi,
  PaymentsMeApi,
  AdminPaymentsApi,
  ProductRequestsApi,
  AdminProductRequestsApi,
  LoyaltyApi,
  AdminLoyaltyApi,
  InboxApi,
  PurchaseOrdersApi,
  ProductAttributesApi,
  ProductDealTypesApi,
  ProductWarrantiesApi,
  ProductWarrantyTypesApi,
  CountriesApi,
  BuyerAddressesApi,
  CompanyLogoApi,
  ProductConditionsApi,
  ProductColorsApi,
  ProductStorageApi,
  DropshippingApi,
  ProductVariantsApi,
  AdminProductVariantsApi,
  ProductCPUsApi,
  ProductRAMsApi,
  ProductScreenSizesApi,
  ProductResolutionsApi,
  ChangeRequestsApi,
  AdminChangeRequestsApi,
  ProductImagesApi,
  ProductDatasheetsApi,
  AppApi,
  CatalogAssistantApi,
  ComparisonsApi,
  CurrenciesApi,
  HealthApi,
  InvoicesApi,
  LikesApi,
  PricelistsApi,
  BankAccountsApi,
  NotificationsApi,
  TwoFactorAuthenticationApi,
  PaymentMethodsApi,
  AdminProductsApi,
  AdminWarrantyProductsApi,
  FeaturedDealsApi,
  AdminFeaturedDealsApi,
  WarrantyClaimsBuyerApi,
  WarrantyClaimsSellerApi,
  AdminWarrantyClaimsApi,
  OrderDisputesBuyerApi,
  AdminOrderDisputesApi,
} from '../api/index';
import type { CreateProductRequestDto, ProductRequestDto, ProductVariantDto } from "../api";

export const apiBase = import.meta.env.VITE_BACKEND_HOST;

const BASE = apiBase;


async function refreshToken(): Promise<string | null> {
  // Create a separate axios instance for refresh to avoid interceptor loops
  // This is critical - must use raw axios, not the intercepted 'api' instance
  const refreshAxios = axios.create({
    baseURL: BASE,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  try {
    const res = await refreshAxios.post('/api/v1/auth/refresh', {});
    const t = res.data?.accessToken ?? null;

    if (t) {
      tokenStore.set(t);
      return t;
    } else {
      tokenStore.set(null);
      return null;
    }
  } catch {
    tokenStore.set(null);
    return null;
  }
}

export function setupApiClient(): AxiosInstance {
  const api = axios.create({ baseURL: BASE, withCredentials: true });

  api.interceptors.request.use((config) => {
    const t = tokenStore.get();
    if (t) {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>).Authorization = `Bearer ${t}`;
    }
    return config;
  });

  api.interceptors.response.use(
    (r) => r,
    async (error) => {
      const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
      const status = error.response?.status;

      // Skip interceptor for refresh endpoint to prevent infinite loops
      if (original?.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }

      // Only handle 401 errors, skip if already retried or not a 401
      if (!original || status !== 401 || original._retry) {
        return Promise.reject(error);
      }

      original._retry = true;

      try {
        const newToken = await tokenStore.beginRefresh(refreshToken);

        if (!newToken) {
          tokenStore.set(null);
          if (!isPublicPath(window.location.pathname)) {
            window.location.href = AppRoutes.LOGIN;
          }
          return Promise.reject(error);
        }

        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch {
        tokenStore.set(null);
        if (!isPublicPath(window.location.pathname)) {
          window.location.href = AppRoutes.LOGIN;
        }
        return Promise.reject(error);
      }
    }
  );

  return api;
}

export const api = setupApiClient();

const config = new Configuration({ basePath: `${BASE}/api/v1` });

export const authSessionService = new AuthenticationSessionApi(config, undefined, api);
export const authPasswordService = new AuthenticationPasswordApi(config, undefined, api);
export const authOtpService = new AuthenticationOTPApi(config, undefined, api);
export const userService = new UserApi(config, undefined, api);
export const productService = new ProductsApi(config, undefined, api);
export const adminProductsService = new AdminProductsApi(config, undefined, api);
export const partnersService = new PartnersApi(config, undefined, api);
export const salesService = new SalesApi(config, undefined, api);
export const documentsService = new DocumentsApi(config, undefined, api);
export const adminUsersService = new AdminUsersApi(config, undefined, api);
export const categoriesService = new CategoriesApi(config, undefined, api);
export const adminCategoriesService = new AdminCategoriesApi(config, undefined, api);
export const brandsService = new BrandsApi(config, undefined, api);
export const paymentsService = new PaymentsApi(config, undefined, api);
export const productRequestsService = new ProductRequestsApi(config, undefined, api);
export const adminProductRequestsService = new AdminProductRequestsApi(config, undefined, api);
export const loyaltyService = new LoyaltyApi(config, undefined, api);
export const adminLoyaltyService = new AdminLoyaltyApi(config, undefined, api);
export const inboxService = new InboxApi(config, undefined, api);
export const purchaseOrdersService = new PurchaseOrdersApi(config, undefined, api);
export const productAttributesService = new ProductAttributesApi(config, undefined, api);
export const productVariantsService = new ProductVariantsApi(config, undefined, api);
export const productDealTypesService = new ProductDealTypesApi(
  config,
  undefined,
  api
);
export const productWarrantiesService = new ProductWarrantiesApi(
  config,
  undefined,
  api
);
export const productWarrantyTypesService = new ProductWarrantyTypesApi(
  config,
  undefined,
  api
);
export const countriesService = new CountriesApi(config, undefined, api);
export const buyerAddressesService = new BuyerAddressesApi(config, undefined, api);
export const companyLogoService = new CompanyLogoApi(config, undefined, api);
export const productConditionsService = new ProductConditionsApi(
  config,
  undefined,
  api
);
export const productColorsService = new ProductColorsApi(config, undefined, api);
export const productStorageService = new ProductStorageApi(
  config,
  undefined,
  api
);
export const dropshippingService = new DropshippingApi(config, undefined, api);
export const productCpusService = new ProductCPUsApi(config, undefined, api);
export const productRamsService = new ProductRAMsApi(config, undefined, api);
export const productScreenSizesService = new ProductScreenSizesApi(config, undefined, api);
export const productResolutionsService = new ProductResolutionsApi(config, undefined, api);
export const changeRequestsService = new ChangeRequestsApi(config, undefined, api);
export const adminChangeRequestsService = new AdminChangeRequestsApi(config, undefined, api);
export const productImagesService = new ProductImagesApi(config, undefined, api);

export const productDatasheetsService = new ProductDatasheetsApi(config, undefined, api);

// Admin AI extractions. The list endpoint goes through SerializePaginated and
// returns `{ data, meta }`, but the generated client types it as
// `Array<AdminAiExtractionListItemDto>` (codegen doesn't model the wrapper).
// The thin shim below preserves the real shape and avoids `as unknown as` casts
// on every caller — same pattern other paginated admin endpoints use.
export interface AdminAiExtractionListItem {
  id: string;
  status: "pending" | "success" | "failed";
  kind: string;
  resourceType: string | null;
  resourceId: string | null;
  sourceFileName: string | null;
  provider: string;
  model: string;
  promptTokens: number | null;
  completionTokens: number | null;
  totalTokens: number | null;
  costUSD: number | null;
  durationMs: number | null;
  errorMessage: string | null;
  createdAt: string;
  user: { id: string | null; fullName: string | null; email: string | null };
}

export interface AiExtractionTotals {
  totalCalls: number;
  successCalls: number;
  failedCalls: number;
  pendingCalls: number;
  successRate: number;
  totalCostUSD: number;
  totalTokens: number;
  avgDurationMs: number | null;
}

export interface AdminAiExtractionStats {
  last7Days: AiExtractionTotals;
  last30Days: AiExtractionTotals;
  allTime: AiExtractionTotals;
  byKind: Array<{ kind: string; calls: number; costUSD: number; totalTokens: number }>;
  topUsers: Array<{
    userId: string | null;
    fullName: string | null;
    email: string | null;
    calls: number;
    costUSD: number;
  }>;
  byWeek: Array<{ weekStart: string; calls: number; costUSD: number }>;
}

export interface AdminAiExtractionsQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  kind?: string;
  userId?: string;
  status?: "pending" | "success" | "failed";
  dateFrom?: string;
  dateTo?: string;
}

export const adminAiExtractionsService = {
  list(query: AdminAiExtractionsQuery = {}) {
    return api.get<{ data: AdminAiExtractionListItem[]; meta: { totalCount: number; currentPage: number; pageCount: number } }>(
      "/api/v1/admin/ai-extractions",
      { params: query },
    );
  },
  stats() {
    return api.get<AdminAiExtractionStats>("/api/v1/admin/ai-extractions/stats");
  },
};

// Admin AI Chat cost tracking — mirrors the extraction service pattern above.
export interface AdminAiChatListItem {
  id: string;
  chatId: string;
  provider: string | null;
  model: string | null;
  promptTokens: number | null;
  completionTokens: number | null;
  totalTokens: number | null;
  costUSD: number | null;
  durationMs: number | null;
  textPreview: string | null;
  createdAt: string;
  user: { id: string | null; fullName: string | null; email: string | null };
}

export interface AiChatTotals {
  totalCalls: number;
  totalCostUSD: number;
  totalTokens: number;
  avgDurationMs: number | null;
}

export interface AdminAiChatStats {
  last7Days: AiChatTotals;
  last30Days: AiChatTotals;
  allTime: AiChatTotals;
  topUsers: Array<{
    userId: string | null;
    fullName: string | null;
    email: string | null;
    calls: number;
    costUSD: number;
  }>;
  byWeek: Array<{ weekStart: string; calls: number; costUSD: number }>;
}

export interface AdminAiChatQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface AdminAiChatConversation {
  chatId: string;
  title: string | null;
  messageCount: number;
  assistantCount: number;
  totalCostUSD: number;
  totalTokens: number;
  firstMessageAt: string;
  lastMessageAt: string;
  lastMessagePreview: string | null;
  user: { id: string | null; fullName: string | null; email: string | null };
}

export interface AdminAiChatConversationMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  model: string | null;
  provider: string | null;
  promptTokens: number | null;
  completionTokens: number | null;
  totalTokens: number | null;
  costUSD: number | null;
  durationMs: number | null;
  createdAt: string;
}

export interface AdminAiChatConversationDetail {
  chatId: string;
  title: string | null;
  user: { id: string | null; fullName: string | null; email: string | null };
  totalCostUSD: number;
  totalTokens: number;
  messages: AdminAiChatConversationMessage[];
}

export interface AdminAiChatPartnerCost {
  partnerId: number;
  userId: string | null;
  totalCostUSD: number;
  last30DaysCostUSD: number;
  last7DaysCostUSD: number;
  totalCalls: number;
  totalTokens: number;
}

export const adminAiChatService = {
  list(query: AdminAiChatQuery = {}) {
    return api.get<{ data: AdminAiChatListItem[]; meta: { totalCount: number; currentPage: number; pageCount: number } }>(
      "/api/v1/admin/ai-chat",
      { params: query },
    );
  },
  stats() {
    return api.get<AdminAiChatStats>("/api/v1/admin/ai-chat/stats");
  },
  conversations(query: AdminAiChatQuery = {}) {
    return api.get<{ data: AdminAiChatConversation[]; meta: { totalCount: number; currentPage: number; pageCount: number } }>(
      "/api/v1/admin/ai-chat/conversations",
      { params: query },
    );
  },
  conversationDetail(chatId: string) {
    return api.get<AdminAiChatConversationDetail>(
      `/api/v1/admin/ai-chat/conversations/${chatId}`,
    );
  },
  partnerCost(partnerId: number) {
    return api.get<AdminAiChatPartnerCost>(
      `/api/v1/admin/ai-chat/user-cost/${partnerId}`,
    );
  },
};

// Recently added or previously unwired services. Each maps 1:1 to a generated
// `*Api` class so calls go through the same axios interceptor (auth refresh,
// CORS, etc.) as everything above.
export const paymentsMeService = new PaymentsMeApi(config, undefined, api);
export const adminPaymentsService = new AdminPaymentsApi(config, undefined, api);
export const adminProductVariantsService = new AdminProductVariantsApi(config, undefined, api);
export const appService = new AppApi(config, undefined, api);
export const chatService = new CatalogAssistantApi(config, undefined, api);

export interface BestDealsQuery {
  limit?: number;
  excludeIds?: string;
}

export interface BestDealItem {
  product: ProductVariantDto;
  supplierName: string;
  supplierVerified: boolean;
  warrantyLabel?: string | null;
  discountPercent?: number | null;
  rank: number;
}

export interface BestDealsResponse {
  updatedAt: string;
  currency: string;
  items: BestDealItem[];
}

export const bestDealsService = {
  getBestDeals(query?: BestDealsQuery) {
    return api.get<BestDealsResponse>("/api/v1/products/best-deals", {
      params: query,
    });
  },
};

export const featuredDealsService = new FeaturedDealsApi(config, undefined, api);
export const adminFeaturedDealsService = new AdminFeaturedDealsApi(config, undefined, api);

export const comparisonsService = new ComparisonsApi(config, undefined, api);
export const currenciesService = new CurrenciesApi(config, undefined, api);
export const healthService = new HealthApi(config, undefined, api);
export const invoicesService = new InvoicesApi(config, undefined, api);
export const likesService = new LikesApi(config, undefined, api);
export const pricelistsService = new PricelistsApi(config, undefined, api);
export const bankAccountsService = new BankAccountsApi(config, undefined, api);
export const notificationsService = new NotificationsApi(config, undefined, api);
export const twoFactorService = new TwoFactorAuthenticationApi(config, undefined, api);
export const paymentMethodsService = new PaymentMethodsApi(config, undefined, api);
export const adminWarrantyProductsService = new AdminWarrantyProductsApi(config, undefined, api);
export const warrantyClaimsBuyerService = new WarrantyClaimsBuyerApi(config, undefined, api);
export const warrantyClaimsSellerService = new WarrantyClaimsSellerApi(config, undefined, api);
export const adminWarrantyClaimsService = new AdminWarrantyClaimsApi(config, undefined, api);
export const orderDisputesBuyerService = new OrderDisputesBuyerApi(config, undefined, api);
export const adminOrderDisputesService = new AdminOrderDisputesApi(config, undefined, api);

export async function updateProductRequest(
  id: string,
  dto: CreateProductRequestDto,
): Promise<AxiosResponse<ProductRequestDto>> {
  return api.patch<ProductRequestDto>(`/api/v1/product-requests/${id}`, dto);
}

export async function deleteProductRequest(id: string): Promise<AxiosResponse<void>> {
  return api.delete(`/api/v1/product-requests/${id}`);
}

// TODO: replace with generated adminProductsService methods after next `npm run generate:api`
export interface UpdateVariantPricingPayload {
  variantId: number;
  pricingTiers: Array<{ minQty: number; price: number }>;
}

export interface UpdateBuyerPricingPayload {
  variants: UpdateVariantPricingPayload[];
}

export async function adminGetProductDetails(templateId: number) {
  return api.get<{ product: ProductVariantDto & { sellerPrice?: number | null; galleryImages?: string[]; galleryImageIds?: number[] }; variants: ProductVariantDto[] }>(
    `/api/v1/admin/products/${templateId}`,
  );
}

export async function adminUpdateProductPricing(
  templateId: number,
  dto: UpdateBuyerPricingPayload,
): Promise<AxiosResponse<void>> {
  return api.patch(`/api/v1/admin/products/${templateId}/pricing`, dto);
}

export async function updateMyProductPricing(
  templateId: number,
  dto: UpdateBuyerPricingPayload,
): Promise<AxiosResponse<void>> {
  return api.patch(`/api/v1/products/${templateId}/pricing`, dto);
}
